import React, { useCallback, useMemo } from 'react'
import { Grid, makeStyles } from '@material-ui/core'
import DazzedParagraph16 from 'components/common/text/DazzedParagraph16'
import SelectInput from 'components/common/SelectInput'
import TriggerToActionArrow from 'components/common/icons/TriggerToActionArrow'
import { useTranslation } from 'react-i18next'
import { TriggerTypes, actionTypes, canSelectNotifyPractice } from 'consts/automatedActionConsts'
import { Message, PatientTag, AutomationsAssignPatient } from 'components/common/icons'
import NeedsAttention from 'components/common/icons/NeedsAttention'
import useRolePermissions from 'hooks/useRolePermissions'
import usePlanLimits from 'hooks/usePlanLimits'
import useFeatureFlags from 'hooks/useFeatureFlags'

const useStyles = makeStyles({
  label: {
    color: 'var(--text-color-11)'
  },
  dropdown: {
    maxHeight: '350px !important'
  }
})

const RuleEditor = ({
  triggerType,
  setTriggerType,
  actionType,
  setActionType,
  savedActionType,
  savedTriggerType,
  isSystem,
  setConditions,
  errors
}) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { permissions } = useRolePermissions()
  const { isHI } = usePlanLimits()
  const { timeBasedRecurringAutomations: timeBasedRecurringAutomationsFF = false } = useFeatureFlags()

  const triggerTypesOptions = useMemo(
    () =>
      Object.values(TriggerTypes)
        // patientEveryXWeeks is gated behind timeBasedRecurringAutomations until the backend
        // (grin-automated-actions-service #77, which provisions the EventBridge schedule) is
        // deployed on this stage - a rule saved before that lands would get no scheduled task and
        // never self-heal. A rule already saved with this trigger keeps showing it regardless (via
        // savedTriggerType), same precedent as canSelectNotifyPractice's savedActionType: removing
        // it from the options list would make SelectInput render the raw slug with no way back.
        .filter(
          triggerTypeValue =>
            triggerTypeValue !== TriggerTypes.PatientEveryXWeeks ||
            timeBasedRecurringAutomationsFF ||
            savedTriggerType === TriggerTypes.PatientEveryXWeeks
        )
        .map(triggerTypeValue => ({
          value: triggerTypeValue,
          label: t(`pages.accountSettings.automations.triggerTypes.${triggerTypeValue}`)
        })),
    [t, timeBasedRecurringAutomationsFF, savedTriggerType]
  )

  const actionTypesOptions = useMemo(
    () =>
      [
        {
          value: actionTypes.SendMessage,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.SendMessage}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              <Message />
            </div>
          ),
          condition: permissions.sendChatMessages && triggerType !== TriggerTypes.LeadCreated
        },
        {
          value: actionTypes.AddPatientTag,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.AddPatientTag}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              <PatientTag />
            </div>
          ),
          condition: true
        },
        {
          value: actionTypes.AssignPatient,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.AssignPatient}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              <AutomationsAssignPatient />
            </div>
          ),
          condition: true
        },
        {
          value: actionTypes.ShowAnnouncement,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.ShowAnnouncement}`),
          icon: <></>,
          condition: permissions.opsDashboardSystemAutomations
        },
        {
          value: actionTypes.NotifyPractice,
          label: t(`pages.accountSettings.automations.actionTypes.${actionTypes.NotifyPractice}`),
          icon: (
            <div style={{ marginTop: 6 }}>
              {/* Sized by ink rather than box: this glyph fills its viewBox edge to edge, while
                  Message ships 16px of ink in a 24px box and the other two ~23px. 20 sits it
                  between them instead of rendering as a small dot. */}
              <NeedsAttention width={20} height={20} />
            </div>
          ),
          // Available to practices on an HI plan, not just system automations — the resulting
          // Needs Attention is flagged with the `automation` reason so its origin stays visible
          // on the patient. savedActionType, not actionType: see canSelectNotifyPractice.
          condition: canSelectNotifyPractice({
            isHiPlan: isHI,
            isSystemRule: isSystem,
            savedActionType
          })
        }
      ].filter(({ condition }) => condition),
    [t, permissions, triggerType, isHI, isSystem, savedActionType]
  )

  const handleTriggerTypeChange = useCallback(
    newTriggerType => {
      setConditions([])
      setTriggerType(newTriggerType)
    },
    [setConditions, setTriggerType]
  )

  return (
    <Grid container alignItems="center">
      <Grid item xs={5}>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <DazzedParagraph16 bold className={classes.label}>
              {t('pages.accountSettings.automations.editorModal.triggerTypeLabel')}
            </DazzedParagraph16>
          </Grid>
          <Grid item>
            <SelectInput
              style={{ bright: true, thick: true }}
              options={triggerTypesOptions}
              value={triggerType}
              onChange={option => handleTriggerTypeChange(option.value)}
              placeholder={t('pages.accountSettings.automations.editorModal.triggerTypePlaceholder')}
              dropdownClassName={classes.dropdown}
              errorMessage={errors.requiredFields?.triggerType}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item>
        <TriggerToActionArrow style={{ marginTop: 15 }} />
      </Grid>
      <Grid item xs={5}>
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <DazzedParagraph16 bold className={classes.label}>
              {t('pages.accountSettings.automations.editorModal.actionTypeLabel')}
            </DazzedParagraph16>
          </Grid>
          <Grid item>
            <SelectInput
              style={{ bright: true, thick: true }}
              options={actionTypesOptions}
              value={actionType}
              onChange={option => setActionType(option.value)}
              placeholder={t('pages.accountSettings.automations.editorModal.actionPlaceholder')}
              dropdownClassName={classes.dropdown}
              errorMessage={errors.requiredFields?.actionType}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  )
}

export default RuleEditor
